import KeyIcon from "@mui/icons-material/KeyOutlined";
import NotRequiredIcon from "@mui/icons-material/ShieldOutlined";
import EnabledIcon from "@mui/icons-material/VerifiedUserOutlined";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../context/ThemeContext.tsx";
import { usePersona } from "../context/usePersona.ts";
import {
  CurrencyCode,
  getCurrency,
  getCurrencyCode,
} from "../i18n/Currency.tsx";
import { getUserDisplayName } from "../shared/userDisplayName.ts";
import { longDate } from "../utils/longDate.ts";
import { ChangePasswordDialog } from "./auth/ChangePasswordDialog.tsx";
import { MfaResetDialog } from "./auth/MfaResetDialog.tsx";
import { useAuth } from "./auth/ParseAuthContext";
import { InitialAvatar } from "./auth/UserAvatar.tsx";
import { SectionLabel } from "./general/SectionLabel.tsx";
import { SegmentedControl } from "./general/SegmentedControl.tsx";
import { TonalBadge } from "./general/TonalBadge.tsx";

/** The page is no wider than this, in px. */
const MAX_WIDTH = 1180;

const card = { borderRadius: 3 } as const;

const CURRENCIES: CurrencyCode[] = ["EUR", "GBP", "USD"];

/** A fact of the account: what it is, and its value. */
const AccountRow = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "96px minmax(0, 1fr)",
      columnGap: 1.5,
      alignItems: "baseline",
      px: 2.5,
      py: 1.375,
      borderTop: 1,
      borderColor: "divider",
    }}
  >
    <Typography
      component="dt"
      variant="caption"
      sx={{ fontWeight: 600, color: "text.secondary" }}
    >
      {label}
    </Typography>
    <Typography
      component="dd"
      variant="body2"
      sx={{ m: 0, fontWeight: 500, overflowWrap: "anywhere" }}
    >
      {value}
    </Typography>
  </Box>
);

/** One of the things a user signs in with: what it is, its state, and what they can do about it. */
const SecurityRow = ({
  icon,
  title,
  badge,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  badge?: ReactNode;
  description: ReactNode;
  action?: ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: 2,
      rowGap: 1.5,
      px: 2.5,
      py: 2,
      borderTop: 1,
      borderColor: "divider",
    }}
  >
    <Box
      aria-hidden
      sx={{
        flex: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 38,
        height: 38,
        borderRadius: 2.25,
        bgcolor: "action.hover",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        <Typography component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {badge}
      </Box>
      <Typography variant="body2" sx={{ mt: 0.25, color: "text.secondary" }}>
        {description}
      </Typography>
    </Box>
    {action}
  </Box>
);

/**
 * The page of the user who is signed in: their account, the currency their
 * amounts are shown in, and what they sign in with — the password, which they
 * can change, and the second factor, which they can reset.
 */
export const UserProfile = () => {
  const { auth, updateUserField } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "appBar.userProfile" });
  const [changingPassword, setChangingPassword] = useState(false);
  // said once the password is changed, for as long as the page stays open
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);
  const { personas, active: activePersona, switchPersona } = usePersona();

  // the pages are drawn for a signed-in user only
  if (auth.kind !== "signedIn") return null;

  const { user } = auth;
  const { username, email, mfaEnabled, mfaConfirmedAt } = user;
  const displayName = getUserDisplayName(user) ?? username;
  const actionButton = {
    flex: "none",
    textTransform: "none",
    fontWeight: 600,
  } as const;

  return (
    <Box component="main" sx={{ px: 3.5, pt: 3.75, pb: 5 }}>
      <Stack spacing={2.25} sx={{ maxWidth: MAX_WIDTH, mx: "auto" }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.015em" }}
          >
            {t("title")}
          </Typography>
          <Typography
            sx={{ mt: 0.75, color: "text.secondary", maxWidth: "70ch" }}
          >
            {t("description")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: 2.25,
          }}
        >
          {/* who is signed in, and how amounts are shown to them */}
          <Stack spacing={2.25} sx={{ flex: "1 1 340px", minWidth: 0 }}>
            <Paper
              component="section"
              variant="outlined"
              sx={{ ...card, overflow: "hidden" }}
            >
              <Box sx={{ px: 2.5, pt: 2 }}>
                <SectionLabel component="h2">{t("account.title")}</SectionLabel>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  px: 2.5,
                  pt: 1.75,
                  pb: 2,
                }}
              >
                <InitialAvatar name={displayName} size={52} />
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    minWidth: 0,
                    fontWeight: 700,
                    letterSpacing: "-.01em",
                    overflowWrap: "anywhere",
                  }}
                >
                  {displayName}
                </Typography>
              </Box>
              {/* the rows hold what the name at the head does not say */}
              <Box component="dl" sx={{ m: 0 }}>
                {username !== displayName && (
                  <AccountRow label={t("username")} value={username} />
                )}
                {email && <AccountRow label={t("email")} value={email} />}
              </Box>
            </Paper>

            {/* a user who only ever works as a law firm has nothing to see */}
            {(personas.length > 1 || personas[0] !== "lawFirm") && (
              <Paper
                component="section"
                variant="outlined"
                sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}
              >
                <SectionLabel component="h2">{t("role.title")}</SectionLabel>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, mb: 1.5, color: "text.secondary" }}
                >
                  {t("role.description")}
                </Typography>
                {personas.length > 1 ? (
                  <SegmentedControl
                    fullWidth
                    ariaLabel={t("role.title")}
                    value={activePersona}
                    options={personas.map((persona) => ({
                      value: persona,
                      label: t(`role.${persona}`),
                    }))}
                    onChange={switchPersona}
                  />
                ) : (
                  <Typography sx={{ fontWeight: 600 }}>
                    {t(`role.${activePersona}`)}
                  </Typography>
                )}
              </Paper>
            )}

            <Paper
              component="section"
              variant="outlined"
              sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}
            >
              <SectionLabel component="h2">{t("currency.title")}</SectionLabel>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, mb: 1.5, color: "text.secondary" }}
              >
                {t("currency.description")}
              </Typography>
              <SegmentedControl
                fullWidth
                ariaLabel={t("currency.title")}
                value={getCurrencyCode(user.currencyCode)}
                options={CURRENCIES.map((code) => ({
                  value: code,
                  label: t("currency.option", {
                    symbol: getCurrency(code).symbol,
                    code: t(`currency.${code}`),
                  }),
                }))}
                onChange={(code) => updateUserField("currencyCode", code)}
              />
            </Paper>
          </Stack>

          {/* what the user signs in with, and what they can change of it */}
          <Paper
            component="section"
            variant="outlined"
            sx={{
              ...card,
              flex: "1.6 1 560px",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2.5, pt: 2, pb: 1.75 }}>
              <SectionLabel component="h2">{t("security.title")}</SectionLabel>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                {t(
                  mfaEnabled
                    ? "security.signIn.withCode"
                    : "security.signIn.passwordOnly",
                )}
              </Typography>
            </Box>
            {passwordChanged && (
              <Alert
                severity="success"
                role="status"
                sx={{ mx: 2.5, mb: 1.75 }}
              >
                {t("security.password.changed")}
              </Alert>
            )}
            <SecurityRow
              icon={<KeyIcon fontSize="small" sx={{ color: "primary.main" }} />}
              title={t("security.password.title")}
              description={t("security.password.description")}
              action={
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setChangingPassword(true)}
                  sx={actionButton}
                >
                  {t("security.password.change")}
                </Button>
              }
            />
            {/* a user has a second factor, or is exempt from having one:
                without either, the app shows its set-up in place of the pages */}
            {mfaEnabled ? (
              <SecurityRow
                icon={
                  <EnabledIcon
                    fontSize="small"
                    sx={{ color: "primary.main" }}
                  />
                }
                title={t("security.mfa.label")}
                badge={
                  <TonalBadge tone={theme.palette.data.positive}>
                    {t("security.mfa.enabled")}
                  </TonalBadge>
                }
                description={
                  <>
                    {mfaConfirmedAt &&
                      `${t("security.mfa.setUp", {
                        date: longDate(mfaConfirmedAt),
                      })} `}
                    {t("security.mfa.resetting")}
                  </>
                }
                action={
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setResettingMfa(true)}
                    sx={actionButton}
                  >
                    {t("security.mfa.reenrol")}
                  </Button>
                }
              />
            ) : (
              <SecurityRow
                icon={
                  <NotRequiredIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                }
                title={t("security.mfa.label")}
                badge={<TonalBadge>{t("security.mfa.notRequired")}</TonalBadge>}
                description={t("security.mfa.exempt")}
              />
            )}
          </Paper>
        </Box>
      </Stack>

      {changingPassword && (
        <ChangePasswordDialog
          onClose={() => setChangingPassword(false)}
          onChanged={() => {
            setChangingPassword(false);
            setPasswordChanged(true);
          }}
        />
      )}
      <MfaResetDialog
        open={resettingMfa}
        onClose={() => setResettingMfa(false)}
        onDisabled={() => setResettingMfa(false)}
      />
    </Box>
  );
};
